<template>
  <vue-resizable
    data-cy="twilio-dialer"
    class="z-[101]"
    :class="{
      invisible: !deviceActive && !isRealDevice && !globalCallSession,
    }"
    drag-selector=".draggable"
    :width="dialerOptions.width"
    :height="deviceHeight"
    :top="dialerOptions.top"
    :left="dialerOptions.left"
    :active="[]"
    @drag:start="dragging = true"
    @drag:end="updatePosition"
  >
    <div
      ref="draggable"
      class="flex flex-col items-start rounded-lg bg-grey-navigation text-white shadow-dialer"
    >
      <!-- this .draggable wrapper is required to not break dragging -->
      <div class="draggable w-full">
        <div
          v-if="activeUserPhoneNumber"
          class="flex items-center justify-between border-b border-white/10 p-4"
        >
          <div>
            {{
              callDirectionIsOutgoing
                ? $t("DIALER.OUTGOING_CALL_FROM")
                : $t("DIALER.INCOMING_CALL_TO")
            }}:
          </div>

          <div
            v-tooltip="$t('COMMON.COPY_TO_CLIPBOARD')"
            @click.stop.prevent="copyToClipboard(activeUserPhoneNumber)"
          >
            {{ activeUserPhoneNumber }}
          </div>
        </div>
        <div
          class="flex w-full items-center space-x-3 border-b border-white/10 p-4 text-white"
          :class="dragging ? 'cursor-grabbing' : 'cursor-grab'"
        >
          <lf-avatar
            :username="contactedName || $t('COMMON.UNKNOWN')"
            :size="40"
          />
          <div class="flex min-w-0 flex-col">
            <span v-tooltipable.themeLight class="truncate font-semibold">
              {{ contactedName || $t("COMMON.UNKNOWN") }}
            </span>
            <span data-cy="to-number" class="text-xs text-label">
              {{ contactedNumber || "-" }}
            </span>
          </div>
        </div>
      </div>
      <div class="call-details flex w-full flex-col space-y-1 p-4">
        <div
          v-for="row in infoRows"
          :key="row.name"
          :data-cy="row.name"
          class="call-session-info-row group"
        >
          <icon-base
            width="12"
            height="12"
            :view-box="row.iconOptions.viewBox"
            :icon="row.iconOptions.icon"
            class="icon"
          />
          <span v-tooltipable.themeLight class="truncate">
            {{ row.value }}
          </span>
          <router-link v-if="row.link" :to="row.link">
            <icon-base
              width="12"
              height="12"
              view-box="0 0 24 24"
              :icon="IconLink"
            />
          </router-link>
        </div>
      </div>
      <div
        v-if="deviceState === TwilioDeviceState.Ongoing || !!globalCallSession"
        id="ongoing-actions"
        class="twilio-dialer-actions"
      >
        <div
          v-if="deviceState === TwilioDeviceState.Ongoing"
          v-tooltip="callIsMuted ? $t('DIALER.UNMUTE') : $t('DIALER.MUTE')"
          class="dialer-round-button bg-white/10"
          @click="toggleMicrophone"
        >
          <icon-base
            :icon="callIsMuted ? IconMicrophoneMute : IconMicrophone"
          />
        </div>
        <div
          v-if="deviceState !== TwilioDeviceState.Incoming"
          v-tooltip="$t('COMMON.DISCONNECT')"
          class="dialer-round-button bg-secondary-red"
          @click="disconnectCall"
        >
          <icon-base
            :icon="IconDialerPhone"
            width="16"
            height="16"
            view-box="0 0 16 16"
            class="rotate-[135deg]"
          />
        </div>
      </div>
      <div
        v-if="deviceState === TwilioDeviceState.Incoming"
        class="twilio-dialer-actions"
      >
        <div
          v-tooltip="$t('DIALER.IGNORE')"
          class="dialer-round-button flex-none bg-white/10"
          @click="ignoreCall"
        >
          <icon-base :icon="callIsSilenced ? IconSpeakerMute : IconSpeaker" />
        </div>
        <lf-button
          type="error"
          class="space-x-2 bg-error"
          centered
          @click="rejectIncomingCall"
        >
          <template #icon>
            <icon-base :icon="IconDialerPhone" class="rotate-[135deg]" />
          </template>
          <span>
            {{ $t("DIALER.DECLINE") }}
          </span>
        </lf-button>
        <lf-button
          type="success"
          class="space-x-2 bg-success"
          centered
          @click="acceptIncomingCall"
        >
          <template #icon>
            <icon-base :icon="IconDialerPhone" />
          </template>
          <span>
            {{ $t("DIALER.ACCEPT") }}
          </span>
        </lf-button>
      </div>
      <template v-if="isRealDevice">
        <twilio-device-select
          :devices="groupedDevices.input"
          :selected-device="device?.audio?.inputDevice?.deviceId"
          type="input"
          @device:select="setDevice"
          @device:refresh="authorizeUseMedia"
        />
        <twilio-device-select
          v-if="device?.audio?.isOutputSelectionSupported"
          :devices="groupedDevices.output"
          :selected-device="selectedOutputDevice"
          type="output"
          @device:select="setDevice"
          @rdevice:refresh="authorizeUseMedia"
        />
      </template>
    </div>
  </vue-resizable>
  <teleport v-if="callDialog && showIncomingLogDialog" to="#modals">
    <communication-log-call-dialog
      class="fixed z-50 shadow-lg"
      :value="callDialog"
      @close-dialog="closeIncomingLogDialog"
    />
  </teleport>
</template>

<script setup lang="ts">
import {
  useBroadcastChannel,
  useDocumentVisibility,
  useStorage,
} from "@vueuse/core";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toRaw,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import VueResizable from "vue-resizable";

import * as Sentry from "@sentry/vue";
import { Call, Device } from "@twilio/voice-sdk";
import { storeToRefs } from "pinia";

import config from "@/config";
import {
  CallSessionStatus,
  CommunicationType,
  PhoneSelectionType,
} from "@/enums/communicationLogs";
import {
  TwilioCallActions,
  TwilioCallDirection,
  TwilioCallErrors,
  TwilioCallEvents,
  TwilioCallQualityWarningEvents,
  TwilioDeviceState,
} from "@/enums/communicationProviders";
import { FullStoryEvent } from "@/enums/events";
import { getAssignedPhoneNumber } from "@/helpers/communication";
import { ensurePositionWithinViewport } from "@/helpers/elements";
import { makeRandomString } from "@/helpers/formatting";
import {
  leaveChannel,
  subscribeToChannelEvents,
} from "@/helpers/websockets/channels";
import { CALL_CHANNEL } from "@/helpers/websockets/common";
import { useAuth } from "@/hooks/auth";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { useTwilio } from "@/hooks/communicationProviders";
import { useFullStory } from "@/hooks/fullStory";
import { useNotification } from "@/hooks/notifications";
import { useLocalStorageSetting } from "@/hooks/options";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import useCallsStore, { type ActiveCallSession } from "@/stores/calls";

import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";
import IconDialerPhone from "@/components/icons/IconDialerPhone.vue";
import IconLink from "@/components/icons/IconLink.vue";
import IconMicrophone from "@/components/icons/IconMicrophone.vue";
import IconMicrophoneMute from "@/components/icons/IconMicrophoneMute.vue";
import IconPersons from "@/components/icons/IconPersons.vue";
import IconProfileSmall from "@/components/icons/IconProfileSmall.vue";
import IconSpeaker from "@/components/icons/IconSpeaker.vue";
import IconSpeakerMute from "@/components/icons/IconSpeakerMute.vue";
import IconDeals from "@/components/icons/sidebar/IconDeals.vue";
import TwilioDeviceSelect from "@/components/twilio/TwilioDeviceSelect.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfAvatar from "@/components/ui/LFAvatar.vue";
import LfButton from "@/components/ui/buttons/LfButton.vue";

import type { ResizableEventValues } from "@/models/common";

// reconnect config for Connection Error and Transport Error
const COOLDOWN_MS = 5_000;
const MAX_CONNECTION_ERROR_BURST = 3;
const BURST_BACKOFF_MS = 5_000;
const INITIAL_BACKOFF_MS = 500;
const RETRY_CONNECT = true;

const INFO_ROW_HEIGHT = 21;
const INFO_ROW_SPACE_Y = 4;
const DEVICE_FIXED_HEIGHT = 105;
const ACTIVE_PHONE_NUMBER_HEIGHT = 53;
const DEVICE_HEIGHT = 38;
const ACTION_SECTION_HEIGHT = 72;
const REJECT_MESSAGE = "reject";
const ACCEPT_MESSAGE = "accept";
const DISCONNECT_MESSAGE = "disconnect";
const RESET_MESSAGE = "reset";
const UNREGISTER_ATTEMPTS = 3;
const DISCONNECT_RETRIES = 3;

type ConvertedCall = Omit<Call, "customParameters"> & {
  customParameters: Record<string, string>;
};

const { twilioAccessToken, fetchTwilioToken } = useTwilio();
const { t } = useI18n();
const { showMessage } = useNotification();
const { publishEvent } = useFullStory();
const { currentUser } = useAuth();
const { activeCallSession, callDialog } = storeToRefs(useCallsStore());
const dialerOptions = useLocalStorageSetting("dialerOptions", {
  top: 0,
  left: 0,
  width: 340,
});
const assignedPhoneNumber = useLocalStorageSetting("assignedPhoneNumber", null);
const documentVisibility = useDocumentVisibility();
const {
  isSupported: isBroadcastChannelSupported,
  data: broadcastTwilioDevice,
  post: twilioDevicePost,
} = useBroadcastChannel({ name: "lf_twilio_active_device" });
const deviceId = ref<string>(makeRandomString());
const activeDevice = useStorage("lf_twilio_active_device", deviceId.value);
const globalCallSession = useStorage<
  ActiveCallSession | ConvertedCall | undefined
>("lf_active_call", null, undefined, {
  serializer: {
    read: (v: unknown) => {
      if (!v) {
        return undefined;
      }
      const parsed = JSON.parse(v.toString());
      const isConvertedCall = (value: {
        customParameters?: unknown;
      }): value is ConvertedCall =>
        Object.hasOwnProperty.call("customParameters", parsed);
      return isConvertedCall(parsed)
        ? (parsed as ConvertedCall)
        : (parsed as ActiveCallSession);
    },
    write: (v: unknown) => JSON.stringify(v),
  },
});
const { data: twilioCallback, post: twilioCallbackPost } = useBroadcastChannel({
  name: "lf_twilio_callback",
});
const copyToClipboard = useClipboardWithMessage();

const device = ref<Device | null>(null);
const unregisterTries = ref(UNREGISTER_ATTEMPTS);
const disconnectRetries = ref(DISCONNECT_RETRIES);
const call = ref<Call | null>(null);
const incomingCall = ref<Call | null>(null);
const showIncomingLogDialog = ref(false);
const updateDeviceToken = ref(false);
const incomingAudio = ref<HTMLAudioElement | null>(null);
const audioPromise = ref<Promise<void> | undefined>(undefined);
const audioDevices = ref<MediaDeviceInfo[] | null>(null);
//reconnect additions
const lastConnectionErrorAt = ref(0);
const consecutiveConnectionErrors = ref(0);
const pendingReconnect = ref(false);
const lastConnectParams = ref<Device.ConnectOptions | null>(null);
const isDialing = ref(false);
const shouldAutoRetry = ref(false);

const draggable = ref<HTMLElement | null>(null);
const deviceOptions = ref<Device.Options>({
  closeProtection: true,
  codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
  tokenRefreshMs: 30_000,
  edge: ["umatilla", "ashburn", "dublin", "singapore", "sao-paulo"],
  maxCallSignalingTimeoutMs: 30_000,
});
const deviceActive = ref(false);
const isRealDevice = ref(true);
const deviceState = ref<TwilioDeviceState>(TwilioDeviceState.Idle);
const dragging = ref(false);
const callIsMuted = ref(false);
const callIsSilenced = ref(false);
const callDirection = ref<TwilioCallDirection | null>(null);

const callDirectionIsOutgoing = computed(
  () =>
    callDirection.value === TwilioCallDirection.Outgoing ||
    !!(globalCallSession.value as ActiveCallSession)?.application_id
);
const callTarget = computed(() =>
  deviceState.value === TwilioDeviceState.Incoming
    ? incomingCall.value
    : call.value
);

const groupedDevices = computed(() => {
  const empty: { input: MediaDeviceInfo[]; output: MediaDeviceInfo[] } = {
    input: [],
    output: [],
  };
  if (!audioDevices.value) {
    return empty;
  }
  return audioDevices.value.reduce((acc, curr) => {
    const type =
      curr.kind === "audioinput"
        ? "input"
        : curr.kind === "audiooutput"
          ? "output"
          : null;
    if (type) {
      acc[type].push(curr);
    }
    return acc;
  }, empty);
});
const selectedOutputDevice = computed(
  () =>
    [...(device.value?.audio?.speakerDevices?.get() ?? new Set())]?.[0]
      ?.deviceId ?? ""
);

const activeUserPhoneNumber = computed(() => {
  const fromNumber =
    (globalCallSession.value as ActiveCallSession)?.data?.from ??
    assignedPhoneNumber.value ??
    PhoneSelectionType.Auto;
  return callDirectionIsOutgoing.value
    ? fromNumber
    : (globalCallSession.value as ConvertedCall)?.customParameters?.to;
});

const contactedName = computed(() =>
  callDirectionIsOutgoing.value
    ? (globalCallSession.value as ActiveCallSession)?.data?.callee?.name
    : (globalCallSession.value as ConvertedCall)?.customParameters
        ?.callerFullName
);
const contactedNumber = computed(() =>
  callDirectionIsOutgoing.value
    ? (globalCallSession.value as ActiveCallSession)?.to
    : (globalCallSession.value as ConvertedCall)?.parameters?.From
);

const dealLink = computed(() =>
  callDirectionIsOutgoing.value ||
  !(globalCallSession.value as ConvertedCall)?.customParameters.applicationId
    ? undefined
    : {
        name: ROUTE_DEAL_PROGRESS,
        params: {
          id: (globalCallSession.value as ConvertedCall)?.customParameters
            .applicationId,
        },
      }
);

const deviceHeight = computed(() => {
  const rowCount = infoRows.value.length;
  const headerHeight = activeUserPhoneNumber.value
    ? ACTIVE_PHONE_NUMBER_HEIGHT
    : 0;
  const variableHeight =
    rowCount * INFO_ROW_HEIGHT + INFO_ROW_SPACE_Y * (rowCount - 1);
  const actionHeight =
    deviceState.value === TwilioDeviceState.Ongoing ? ACTION_SECTION_HEIGHT : 0;
  const devicesHeight =
    DEVICE_HEIGHT * (device.value?.audio?.isOutputSelectionSupported ? 2 : 1);
  return (
    DEVICE_FIXED_HEIGHT +
    headerHeight +
    variableHeight +
    actionHeight +
    devicesHeight
  );
});

const infoRows = computed(() => {
  const sections = [
    {
      name: "callee-name",
      iconOptions: {
        icon: IconProfileSmall,
        viewBox: "0 0 16 16",
      },
      value: callDirectionIsOutgoing.value
        ? (globalCallSession.value as ActiveCallSession)?.data?.callee?.name
        : (globalCallSession.value as ConvertedCall)?.customParameters
            .callerFullName,
    },
    {
      name: "business-name",
      iconOptions: {
        icon: IconDeals,
        viewBox: "10 10 20 20",
      },
      value: callDirectionIsOutgoing.value
        ? (globalCallSession.value as ActiveCallSession)?.data?.call_sessionable
            ?.business?.business_legal_name
        : (globalCallSession.value as ConvertedCall)?.customParameters
            .callerBusinessName,
      link: dealLink.value,
    },
    {
      name: "partner-name",
      iconOptions: {
        icon: IconPersons,
        viewBox: "10 10 20 20",
      },
      value: callDirectionIsOutgoing.value
        ? (globalCallSession.value as ActiveCallSession)?.partner
        : (globalCallSession.value as ConvertedCall)?.customParameters
            .callerIntegrationPartner,
    },
  ];
  return sections.filter((section) => !!section.value);
});

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const safeConnect = async (params: Device.ConnectOptions) => {
  const rawDevice = toRaw(device.value);
  if (!rawDevice) {
    return;
  }
  return rawDevice.connect(params);
};

const handleConnectionErrorReconnection = async (
  maybeReconnectParams?: Device.ConnectOptions
) => {
  const now = Date.now();
  if (
    pendingReconnect.value ||
    now - lastConnectionErrorAt.value < COOLDOWN_MS
  ) {
    return;
  }
  pendingReconnect.value = true;
  try {
    // track burstiness
    if (now - lastConnectionErrorAt.value < 15_000) {
      consecutiveConnectionErrors.value += 1;
    } else {
      consecutiveConnectionErrors.value = 1;
    }
    lastConnectionErrorAt.value = now;

    await setupAndRegisterDevice();

    if (consecutiveConnectionErrors.value >= MAX_CONNECTION_ERROR_BURST) {
      await sleep(BURST_BACKOFF_MS);
    }

    // Optional outbound retry
    if (
      RETRY_CONNECT &&
      isDialing.value &&
      shouldAutoRetry.value &&
      maybeReconnectParams
    ) {
      let delay = INITIAL_BACKOFF_MS;
      for (let i = 0; i < 3; i++) {
        try {
          await safeConnect(maybeReconnectParams);
          deviceActive.value = true;
          break;
        } catch (err) {
          if (i === 2) {
            isDialing.value = false;
            deviceActive.value = false;
            throw err;
          }
          await sleep(delay + Math.floor(Math.random() * 150));
          delay *= 2;
        }
      }
    }
  } finally {
    pendingReconnect.value = false;
  }
};

const updatePosition = async (position: ResizableEventValues) => {
  dragging.value = false;
  dialerOptions.value = {
    ...dialerOptions.value,
    left: position.left,
    top: position.top,
  };
  await repositionAsNeeded();
};

const repositionAsNeeded = async () => {
  const draggableBounding = draggable.value?.getBoundingClientRect();
  const updates = await ensurePositionWithinViewport(
    dialerOptions.value,
    draggableBounding
  );
  dialerOptions.value = {
    ...dialerOptions.value,
    left: updates.x,
    top: updates.y,
  };
};

const handleDialerError = async (err: Record<string, string>) => {
  if (
    err.name === TwilioCallErrors.AccessTokenExpired ||
    err.name === TwilioCallErrors.AccessTokenInvalid
  ) {
    updateDeviceToken.value = true;
    await fetchTwilioToken();
    setupAndRegisterDevice();
  } else {
    if (err.name === TwilioCallErrors.ConnectionError) {
      handleConnectionErrorReconnection(lastConnectParams.value || undefined);
      return;
    }
    if (err.name === TwilioCallErrors.TransportError) {
      handleConnectionErrorReconnection();
      return;
    }
    config.env === "production"
      ? Sentry.captureException(err)
      : console.error("Twilio dialer error", err);
    showMessage(
      `${t("TWILIO.ERROR")}: ${err.description} - ${err.explanation}`,
      "error"
    );
    globalCallSession.value = null;
  }
};

const registerDeviceListeners = () => {
  device.value?.on(TwilioCallEvents.Error, (err: Record<string, string>) =>
    handleDialerError(err)
  );
  device.value?.on(TwilioCallEvents.TokenWillExpire, async () => {
    updateDeviceToken.value = true;
    await fetchTwilioToken();
    setupAndRegisterDevice();
  });
  device.value?.on(TwilioCallEvents.Incoming, handleIncomingCall);
  device.value?.on?.(
    TwilioCallEvents.Unregistered,
    handleConnectionErrorReconnection
  );
  device.value?.on?.(
    TwilioCallEvents.TransportClosed,
    handleConnectionErrorReconnection
  );
};

const registerCallListeners = () => {
  if (!call.value) {
    return;
  }
  call.value.on(TwilioCallActions.Accept, () => {
    isDialing.value = false;
    setOngoingCall();
  });
  call.value.on(TwilioCallActions.Disconnect, () => {
    shouldAutoRetry.value = false;
    isDialing.value = false;
    clearCallSession();
  });
  call.value.on(TwilioCallActions.Cancel, () => {
    shouldAutoRetry.value = false;
    isDialing.value = false;
    clearCallSession();
  });
  call.value.on(TwilioCallActions.Reject, () => {
    shouldAutoRetry.value = false;
    isDialing.value = false;
    clearCallSession();
  });
  call.value.on(TwilioCallActions.Mute, toggleCallMuted);
  call.value.on(TwilioCallActions.Reconnecting, showConnectionLostMessage);
  call.value.on(TwilioCallActions.Reconnected, showReconnectedMessage);
  call.value.on(TwilioCallActions.Warning, showCallQualityWarning);
  call.value.on(TwilioCallActions.WarningCleared, showCallQualityRestored);
};

const removeCallListeners = () => {
  if (!call.value) {
    return;
  }
  call.value.removeAllListeners();
};

const registerCallerListeners = () => {
  // if the caller cancels the call before we accept or reject, clean up
  if (!incomingCall.value) {
    return;
  }
  incomingCall.value.on(TwilioCallActions.Disconnect, clearIncomingCall);
  incomingCall.value.on(TwilioCallActions.Reject, clearIncomingCall);
  incomingCall.value.on(TwilioCallActions.Cancel, clearIncomingCall);
  incomingCall.value.on(TwilioCallActions.Error, clearIncomingCall);
};

const removeCallerListeners = () => {
  if (!incomingCall.value) {
    return;
  }
  incomingCall.value.removeAllListeners();
};

const initiateCallSession = async () => {
  if (!device.value || !activeCallSession.value?.data) {
    showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    return;
  }
  deviceActive.value = true;
  const payload: Device.ConnectOptions = {
    params: {
      To: activeCallSession.value.to,
      sessionId: activeCallSession.value.data.session_id ?? "",
    },
  };
  const from = getAssignedPhoneNumber(assignedPhoneNumber.value);
  if (from && payload.params) {
    payload.params.phoneNumber = from;
  }
  isDialing.value = true;
  shouldAutoRetry.value = true;
  lastConnectParams.value = payload;
  try {
    call.value = await toRaw(device.value).connect(payload);
    callDirection.value = TwilioCallDirection.Outgoing;

    if (activeCallSession.value?.data?.id) {
      subscribeToChannelEvents(
        CALL_CHANNEL,
        activeCallSession.value.data.id.toString()
      );
    }

    registerCallListeners();
    publishEvent(FullStoryEvent.FS_TWILIO_OUTGOING_CALL);
  } catch (e) {
    isDialing.value = false;
    deviceActive.value = false;
    showMessage(t("COMMON.ERROR_OCCURRED"), "error");
  }
};

const storeCallSession = (outgoing: boolean) => {
  if (outgoing) {
    globalCallSession.value = activeCallSession.value;
    return;
  }

  globalCallSession.value = {
    ...callTarget.value,
    customParameters: Object.fromEntries(
      callTarget.value?.customParameters || new Map()
    ),
  } as ConvertedCall;
  return;
};

const handleIncomingCall = async (call: Call) => {
  const isActive =
    activeDevice.value === deviceId.value &&
    documentVisibility.value === "visible";

  callIsSilenced.value = false;
  if (isActive && !!currentUser) {
    playIncomingCallSound();
  }
  registerDeviceListeners();
  incomingCall.value = call;
  registerCallerListeners();
  deviceState.value = TwilioDeviceState.Incoming;
  deviceActive.value = true;
  callDirection.value = TwilioCallDirection.Incoming;
  publishEvent(FullStoryEvent.FS_TWILIO_INCOMING_CALL);
};

const handleCallNotesModal = (customParameters: Map<string, string>) => {
  const callerIntegrationPartner =
    customParameters.get("callerIntegrationPartner") ?? "";
  const callerBusinessName = customParameters.get("callerBusinessName") ?? "";
  const callSessionId = customParameters.get("callSessionId");

  if (!callSessionId) {
    return;
  }

  showIncomingLogDialog.value = true;

  callDialog.value = {
    id: Number(callSessionId),
    type: CommunicationType.call,
    data: {
      id: Number(callSessionId),
      session_id: callSessionId,
      status: CallSessionStatus.InProgress,
      call_disposition: "",
      call_sessionable: {
        business: {
          business_legal_name: callerBusinessName,
        },
        client_name: callerIntegrationPartner,
      },
      is_outgoing: false,
    },
  };
};

const closeIncomingLogDialog = () => {
  showIncomingLogDialog.value = false;
  callDialog.value = null;
};

const playIncomingCallSound = () => {
  if (!incomingAudio.value) {
    incomingAudio.value = new Audio("/sounds/incoming.mp3");
    incomingAudio.value.loop = true;
    incomingAudio.value.volume = 1;
  }

  try {
    audioPromise.value = incomingAudio.value.play();
  } catch (error) {
    showMessage(t("TWILIO.FAILED_TO_PLAY_SOUND"), "error");
  }
};

const stopIncomingCallSound = () => {
  audioPromise.value?.then(() => {
    if (incomingAudio.value) {
      incomingAudio.value.pause();
      incomingAudio.value.currentTime = 0;
    }
  });
};

const rejectIncomingCall = async () => {
  if (!device.value || !incomingCall.value) {
    showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    return;
  }
  stopIncomingCallSound();
  toRaw(incomingCall.value).reject();
  clearCallSession();
  twilioDevicePost(REJECT_MESSAGE);
};

const acceptIncomingCall = async () => {
  if (!device.value || !incomingCall.value) {
    showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    return;
  }
  stopIncomingCallSound();
  // promote the incoming call to the call so follow-up actions are DRY
  call.value = await toRaw(device.value).connect({
    connectToken: incomingCall.value.connectToken,
  });
  registerCallListeners();
  handleCallNotesModal(call.value.customParameters);
  twilioDevicePost(ACCEPT_MESSAGE);
};

const showConnectionLostMessage = (error: Record<string, string>) => {
  handleDialerError(error);
  showMessage(t("TWILIO.CONNECTIVITY_LOST", "error"));
};

const showReconnectedMessage = () => {
  showMessage("TWILIO.CONNECTIVITY_RESTORED");
};

const showCallQualityWarning = () => {
  showMessage(t("TWILIO.CALL_QUALITY_DEGRADED"), "warning");
};

const showCallQualityRestored = (reason: string) => {
  if (TwilioCallQualityWarningEvents.ConstantAudioInputLevel === reason) {
    return;
  }
  showMessage(t("TWILIO.CALL_QUALITY_RESTORED"));
};

const setOngoingCall = () => {
  deviceState.value = TwilioDeviceState.Ongoing;
};

const toggleCallMuted = (isMuted: boolean) => {
  callIsMuted.value = isMuted;
};

const clearCallSession = (clearGlobal = true) => {
  deviceActive.value = false;
  deviceState.value = TwilioDeviceState.Idle;
  isDialing.value = false;
  shouldAutoRetry.value = false;
  removeCallListeners();
  removeCallerListeners();
  call.value = null;
  incomingCall.value = null;
  callDirection.value = null;
  callIsMuted.value = false;
  leaveChannel(CALL_CHANNEL);
  stopIncomingCallSound();
  if (clearGlobal) {
    globalCallSession.value = null;
  }
};

const clearIncomingCall = () => {
  setTimeout(() => {
    incomingCall.value = null;
  }, 500);
  stopIncomingCallSound();
  globalCallSession.value = null;
  deviceActive.value = false;
};

const toggleMicrophone = () => {
  toRaw(call.value)?.mute(!callIsMuted.value);
};

const ignoreCall = () => {
  toRaw(call.value)?.ignore();
  stopIncomingCallSound();
  callIsSilenced.value = true;
};

const disconnectCall = () => {
  if (
    isRealDevice.value ||
    call.value ||
    device.value?.isBusy ||
    disconnectRetries.value <= 0
  ) {
    toRaw(call.value)?.disconnect();
    stopIncomingCallSound();
    twilioDevicePost(DISCONNECT_MESSAGE);
    globalCallSession.value = null;
    twilioCallbackPost(RESET_MESSAGE);
    twilioCallback.value = "";
    setTimeout(() => (disconnectRetries.value = DISCONNECT_RETRIES), 5000);
    return;
  }
  if (disconnectRetries.value >= 0) {
    disconnectRetries.value--;
    disconnectCall();
    twilioCallbackPost(DISCONNECT_MESSAGE);
    if (disconnectRetries.value === 0) {
      publishEvent(FullStoryEvent.FS_TWILIO_DISCONNECT_RETRIES_LIMIT);
    }
  }
};

const setupAndRegisterDevice = async () => {
  if (!audioDevices.value) {
    await authorizeUseMedia();
  }
  if (device.value) {
    if (
      updateDeviceToken.value &&
      device.value.state !== Device.State.Registering
    ) {
      device.value.updateToken(twilioAccessToken.value);
      updateDeviceToken.value = false;
    }
    broadcastActiveDevice();
    return;
  }
  if (twilioAccessToken.value) {
    try {
      device.value = new Device(twilioAccessToken.value, deviceOptions.value);
      device.value?.audio?.incoming(false);
      registerDeviceListeners();
      device.value.register();
      broadcastActiveDevice();
    } catch (err) {
      Sentry.captureException(err);
    }
  }
};

const unregisterDevice = () => {
  if (device.value) {
    if (device.value.state === Device.State.Registered) {
      device.value?.unregister();
      device.value?.removeAllListeners();
      unregisterTries.value = UNREGISTER_ATTEMPTS;
      return;
    }
    if (device.value.state !== Device.State.Unregistered) {
      // the device may be in "registering" state so we should try to unregister again
      // since we do want to unregister even if it hasn't fully resolved yet
      // but limit the number of attempts here
      unregisterTries.value--;
      if (unregisterTries.value >= 0) {
        setTimeout(unregisterDevice, 1000);
      }
    }
  }
};

const broadcastActiveDevice = () => {
  activeDevice.value = deviceId.value;
  if (isBroadcastChannelSupported.value) {
    twilioDevicePost(deviceId.value);
  }
};

const authorizeUseMedia = async () => {
  try {
    const permissions = await navigator.permissions.query({
      name: "microphone",
    });
    if (permissions?.state !== "granted") {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
    await navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => (audioDevices.value = devices));
  } catch (err) {
    showMessage(t("TWILIO.DEVICE_AUTHORIZATION_ERROR"), "error");
    Sentry.captureException(err);
  }
};

const setDevice = (selectedDevice: string, type: "input" | "output") => {
  if (type === "input") {
    device.value?.audio?.setInputDevice(selectedDevice);
    return;
  }
  device.value?.audio?.speakerDevices?.set(selectedDevice);
};

watch(
  twilioAccessToken,
  async (newValue, oldValue) => {
    if (!newValue) {
      updateDeviceToken.value = true;
      await fetchTwilioToken();
      // watcher will fire again after we have token, return
      return;
    }
    if (newValue === oldValue || !!activeCallSession.value) {
      return;
    }
    setupAndRegisterDevice();
  },
  { immediate: true }
);

watch(activeCallSession, (newValue) => {
  if (newValue) {
    initiateCallSession();
    storeCallSession(true);
    return;
  }
  deviceActive.value = false;
});

watch(callTarget, (newValue) => {
  if (
    newValue &&
    [TwilioDeviceState.Ongoing, TwilioDeviceState.Incoming].includes(
      deviceState.value
    )
  ) {
    storeCallSession(false);
  }
});

watch([deviceHeight, deviceState], async () => {
  await nextTick(repositionAsNeeded);
});

watch(documentVisibility, (newValue, oldValue) => {
  if (newValue === "visible" && oldValue === "hidden") {
    setupAndRegisterDevice();
  }
});

watch(broadcastTwilioDevice, (updatedMessage) => {
  if (!updatedMessage) {
    return;
  }
  unregisterDevice();
  if (
    [REJECT_MESSAGE, ACCEPT_MESSAGE, DISCONNECT_MESSAGE].some(
      (message) => message === updatedMessage
    )
  ) {
    clearCallSession(false);
    return;
  }
  activeDevice.value = String(updatedMessage);
});

watch(twilioCallback, (callbackMessage) => {
  if (!callbackMessage) {
    return;
  }
  if (callbackMessage === DISCONNECT_MESSAGE) {
    disconnectCall();
  }
  if (callbackMessage === RESET_MESSAGE) {
    twilioCallbackPost("");
  }
});

watch(
  () => currentUser,
  () => {
    if (!currentUser) {
      unregisterDevice();
      stopIncomingCallSound();
    }
  }
);

watch(
  call,
  (newValue) => {
    isRealDevice.value = !!newValue;
  },
  {
    immediate: true,
  }
);

onMounted(async () => {
  authorizeUseMedia();
  const element = draggable.value?.getBoundingClientRect();
  if (
    !element ||
    (dialerOptions.value.top !== 0 && dialerOptions.value.left !== 0)
  ) {
    return;
  }
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  dialerOptions.value = {
    ...dialerOptions.value,
    top: viewportHeight - element.height - 16,
    left: viewportWidth - element.width - 24,
  };
  await repositionAsNeeded();
});

onBeforeUnmount(() => {
  unregisterDevice();
});

addEventListener("beforeunload", () => {
  // if this device is on a call unset the global call
  if (device.value?.isBusy) {
    globalCallSession.value = null;
  }
});
</script>

<style>
@reference "@/assets/tailwind.css";

.call-session-info-row {
  @apply flex h-[23px] items-center justify-start space-x-2;

  .icon {
    @apply flex-none text-label;
  }
}

.dialer-round-button {
  @apply flex size-10 cursor-pointer items-center justify-center rounded-full;
}

.twilio-dialer-actions {
  @apply flex w-full items-center justify-center space-x-3 p-4;
}
</style>
